import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Layout from '../components/Layout';
import StatusChip, { RenewalChip, TierChip } from '../components/StatusChip';
import { bands, renewalStatuses, BILLING_FREQUENCIES } from '../theme';
import api from '../services/api';
import { useCan } from '../hooks/useMe';
import useSessionState from '../hooks/useSessionState';
import { dayOf, dayValue } from '../utils/dates';
import { useI18n } from '../i18n';

// Columnas que se editan en la tabla: al hacer clic no navegan.
const EDITABLE_FIELDS = ['cs_owner_id', 'sales_owner_id', 'renewal_date', 'renewal_status', 'billing_frequency'];

const VIEWS = [
  { key: 'mine',       scope: 'managed' },
  { key: 'enterprise', scope: 'managed' },
  { key: 'businessPlus', scope: 'managed' },
  { key: 'renewing',   scope: 'managed' },
  { key: 'unowned',    scope: 'managed' },
  { key: 'inactive',   scope: 'managed' },
  { key: 'all',        scope: 'managed' },
  { key: 'free',       scope: 'free' },
  { key: 'students',   scope: 'students' },
  { key: 'internal',   scope: 'internal' },
  { key: 'toDeactivate', scope: 'inactive' },
];

// Seat activation as a ratio with a meter — the column that catches a large
// account kept alive by one admin.
function SeatMeter({ row }) {
  const pct = row.total_users ? row.active_30d / row.total_users : 0;
  const color = bands[row.band].color;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end', width: '100%' }}>
      <Box sx={{ width: 46, height: 5, borderRadius: 3, backgroundColor: '#EDE6E3', overflow: 'hidden' }}>
        <Box sx={{ width: `${Math.max(pct * 100, 2)}%`, height: '100%', backgroundColor: color }} />
      </Box>
      <Box sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500, minWidth: 44, textAlign: 'right' }}>
        {row.active_30d}/{row.total_users}
      </Box>
    </Box>
  );
}

export default function Accounts() {
  const navigate = useNavigate();
  const { t, tFlag, fmtDate, lang } = useI18n();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState(null);
  const [owners, setOwners] = useState([]);
  const canEditCs = useCan('edit_cs');
  const canEditCommercial = useCan('edit_commercial');

  useEffect(() => {
    api.get('/api/owners').then((res) => setOwners(res.data)).catch(() => setOwners([]));
  }, []);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  // Filtros y estado de la tabla: se conservan al abrir una cuenta y volver.
  const [view, setView] = useSessionState('accounts.view', 'all');
  const [q, setQ] = useSessionState('accounts.q', '');
  const [source, setSource] = useSessionState('accounts.source', '');
  const [sortModel, setSortModel] = useSessionState('accounts.sort', []);
  const [filterModel, setFilterModel] = useSessionState('accounts.filter', { items: [] });
  const [paginationModel, setPaginationModel] = useSessionState('accounts.page', { page: 0, pageSize: 25 });

  const scope = VIEWS.find((v) => v.key === view)?.scope || 'managed';

  useEffect(() => {
    setLoading(true);
    api.get('/api/accounts', { params: { scope } })
      .then((res) => { setRows(res.data.accounts); setCounts(res.data.counts); })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [scope]);

  const filtered = useMemo(() => {
    const quarterEnd = new Date();
    quarterEnd.setMonth(quarterEnd.getMonth() + 3);

    return rows.filter((r) => {
      if (source && r.source_db !== source) return false;
      if (q && !(r.organization_name || '').toLowerCase().includes(q.toLowerCase())) return false;
      switch (view) {
        case 'enterprise':   return r.plan === 'ENTERPRISE';
        case 'businessPlus': return r.plan === 'BUSINESS_PLUS';
        case 'renewing':   return r.renewal_date && dayOf(r.renewal_date) <= quarterEnd;
        case 'unowned':    return r.band === 'at_risk' && !r.cs_owner_id;
        case 'inactive':   return r.flags.some((f) => f.code === 'never_activated');
        case 'mine':       return Boolean(r.cs_owner_id);
        default:           return true;
      }
    });
  }, [rows, view, q, source]);

  const ownerOptions = (roles) => [
    { value: '', label: t('common.unassigned') },
    ...owners
      .filter((o) => roles.includes(o.app_role))
      .map((o) => ({ value: o.id, label: o.display_name })),
  ];

  // Guarda sólo lo que cambió. El score no se recalcula aquí: depende de datos
  // de producto, así que la fila conserva el suyo hasta la siguiente carga.
  const processRowUpdate = async (newRow, oldRow) => {
    const changes = {};
    for (const field of ['cs_owner_id', 'sales_owner_id', 'renewal_status', 'billing_frequency']) {
      if (newRow[field] !== oldRow[field]) {
        changes[field] = newRow[field] === '' ? null : newRow[field];
      }
    }
    const before = dayValue(oldRow.renewal_date);
    const after = dayValue(newRow.renewal_date);
    if (before !== after) changes.renewal_date = after;
    if (!Object.keys(changes).length) return oldRow;
    setSaveFailed(false);

    const { data } = await api.patch(`/api/accounts/${newRow.tenant_id}`, changes, {
      params: { source: newRow.source_db },
    });

    // Confirmar una renovación mueve fechas en el servidor (última renovación y
    // siguiente vencimiento): se toman de la respuesta, no de lo que se envió.
    const saved = {
      ...newRow, ...changes,
      renewal_status: data.renewal_status ?? changes.renewal_status ?? newRow.renewal_status,
      renewal_date: data.renewal_date ?? newRow.renewal_date,
      last_renewal_date: data.last_renewal_date ?? newRow.last_renewal_date,
    };
    saved.cs_owner = owners.find((o) => o.id === saved.cs_owner_id)?.display_name || null;
    saved.sales_owner = owners.find((o) => o.id === saved.sales_owner_id)?.display_name || null;
    setRows((rs) => rs.map((r) =>
      r.tenant_id === saved.tenant_id && r.source_db === saved.source_db ? saved : r));
    return saved;
  };

  const columns = useMemo(() => [
    {
      field: 'organization_name', headerName: t('accounts.columns.organization'), flex: 2, minWidth: 230,
      renderCell: ({ row }) => (
        <Box sx={{ py: 0.8, lineHeight: 1.4 }}>
          <Box sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.8 }}>
            {row.organization_name}
            {row.is_inactive && (
              <Box component="span" sx={{
                fontSize: 9.5, fontWeight: 600, letterSpacing: '0.11em',
                backgroundColor: '#AE5B61', color: '#fff', px: 0.8, py: '1px', borderRadius: '3px',
              }}>{t('accounts.inactiveBadge')}</Box>
            )}
            {row.is_student && (
              <Box component="span" sx={{
                fontSize: 9.5, fontWeight: 600, letterSpacing: '0.11em',
                backgroundColor: '#847782', color: '#fff', px: 0.8, py: '1px', borderRadius: '3px',
              }}>{t('accounts.studentBadge')}</Box>
            )}
            {row.is_internal && (
              <Box component="span" sx={{
                fontSize: 9.5, fontWeight: 600, letterSpacing: '0.11em',
                backgroundColor: '#531253', color: '#fff', px: 0.8, py: '1px', borderRadius: '3px',
              }}>{t('accounts.internalBadge')}</Box>
            )}
            {!row.has_name && (
              <Box component="span" sx={{ fontSize: 11, color: '#AE5B61' }}>{t('accounts.noName')}</Box>
            )}
          </Box>
          <Box sx={{ fontSize: 11.5, color: '#847782' }}>
            {t('accounts.createdOn', {
              date: fmtDate(row.created_at),
              source: t(`sources.${row.source_db}`),
            })}
          </Box>
          {row.flags.length > 0 && (
            <Box sx={{ fontSize: 11.5, color: '#AE5B61' }}>▲ {row.flags.map(tFlag).join(' · ')}</Box>
          )}
        </Box>
      ),
    },
    {
      field: 'subscription_type', headerName: t('accounts.columns.subscription'), width: 130,
      renderCell: ({ row }) => <TierChip tier={row.plan} />,
    },
    {
      field: 'active_30d', headerName: t('accounts.columns.seatsActive'), width: 140, align: 'right', headerAlign: 'right',
      renderCell: ({ row }) => <SeatMeter row={row} />,
    },
    {
      field: 'cs_owner_id', headerName: t('accounts.columns.csOwner'), width: 150,
      editable: canEditCs,
      type: 'singleSelect',
      valueOptions: ownerOptions(['cs_agent', 'admin']),
      valueGetter: (value) => value ?? '',
      renderCell: ({ value }) => {
        const name = owners.find((o) => o.id === value)?.display_name;
        return name || <Box sx={{ color: '#AE5B61', fontWeight: 500 }}>{t('common.unassigned')}</Box>;
      },
    },
    {
      field: 'sales_owner_id', headerName: t('accounts.columns.salesOwner'), width: 150,
      editable: canEditCommercial,
      type: 'singleSelect',
      valueOptions: ownerOptions(['sales', 'admin']),
      valueGetter: (value) => value ?? '',
      renderCell: ({ value }) => {
        const name = owners.find((o) => o.id === value)?.display_name;
        return name || <Box sx={{ color: '#847782' }}>—</Box>;
      },
    },
    {
      field: 'renewal_date', headerName: t('accounts.columns.renewal'), width: 140,
      editable: canEditCommercial,
      type: 'date',
      valueGetter: (value) => dayOf(value),
      renderCell: ({ value }) => {
        if (!value) return <Box sx={{ color: '#847782' }}>—</Box>;
        const soon = (value - Date.now()) / 86400000 <= 90;
        return (
          <Box sx={{ color: soon ? '#AE5B61' : 'inherit', fontWeight: soon ? 500 : 400 }}>
            {fmtDate(value)}
          </Box>
        );
      },
    },
    {
      field: 'renewal_status', headerName: t('accounts.columns.renewalStatus'), width: 145,
      editable: canEditCommercial,
      type: 'singleSelect',
      valueOptions: Object.keys(renewalStatuses).map((key) => ({
        value: key, label: t(`renewalStatus.${key}`),
      })),
      renderCell: ({ value }) => <RenewalChip status={value} />,
    },
    {
      field: 'billing_frequency', headerName: t('accounts.columns.billingFrequency'), width: 120,
      editable: canEditCommercial,
      type: 'singleSelect',
      valueOptions: [
        { value: '', label: '—' },
        ...BILLING_FREQUENCIES.map((key) => ({ value: key, label: t(`billingFrequency.${key}`) })),
      ],
      valueGetter: (value) => value ?? '',
      renderCell: ({ value }) => value
        ? t(`billingFrequency.${value}`)
        : <Box sx={{ color: '#847782' }}>—</Box>,
    },
    {
      field: 'last_renewal_date', headerName: t('accounts.columns.lastRenewal'), width: 140,
      type: 'date',
      valueGetter: (value) => dayOf(value),
      renderCell: ({ value }) => value
        ? fmtDate(value)
        : <Box sx={{ color: '#847782' }}>—</Box>,
    },
    {
      field: 'score', headerName: t('accounts.columns.score'), width: 80, type: 'number',
      renderCell: ({ row }) => (
        <Box sx={{ fontWeight: 600, color: bands[row.band].color }}>{row.score}</Box>
      ),
    },
    {
      field: 'band', headerName: t('accounts.columns.health'), width: 110,
      renderCell: ({ value }) => <StatusChip band={value} />,
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [lang, owners, canEditCs, canEditCommercial]);

  return (
    <Layout>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        {VIEWS.map((v) => (
          <Chip
            key={v.key}
            label={
              counts && v.scope !== 'managed' && counts[v.scope] != null
                ? `${t(`accounts.views.${v.key}`)} · ${counts[v.scope]}`
                : t(`accounts.views.${v.key}`)
            }
            size="small"
            onClick={() => setView(v.key)}
            sx={{
              fontSize: 12.5, borderRadius: '14px',
              backgroundColor: view === v.key ? '#531253' : '#FFFFFF',
              color: view === v.key ? '#fff' : '#847782',
              border: '1px solid #E4E0E2',
              fontWeight: view === v.key ? 500 : 400,
            }}
          />
        ))}
        <TextField
          select size="small"
          value={source} onChange={(e) => setSource(e.target.value)}
          SelectProps={{ displayEmpty: true }}
          sx={{ ml: 'auto', minWidth: 180, backgroundColor: '#fff' }}
        >
          <MenuItem value="">{t('accounts.allSources')}</MenuItem>
          <MenuItem value="mpindustries">{t('sources.mpindustries')}</MenuItem>
          <MenuItem value="mpservices">{t('sources.mpservices')}</MenuItem>
        </TextField>
        <TextField
          size="small" placeholder={t('accounts.search')}
          value={q} onChange={(e) => setQ(e.target.value)}
          sx={{ minWidth: 220, backgroundColor: '#fff' }}
        />
      </Box>

      {failed && <Alert severity="error" sx={{ mb: 2 }}>{t('accounts.loadError')}</Alert>}

      {saveFailed && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveFailed(false)}>
          {t('accounts.saveError')}
        </Alert>
      )}

      <DataGrid
        rows={filtered}
        columns={columns}
        getRowId={(row) => `${row.source_db}:${row.tenant_id}`}
        loading={loading}
        autoHeight
        rowHeight={64}
        disableRowSelectionOnClick
        editMode="cell"
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={() => setSaveFailed(true)}
        pageSizeOptions={[25, 50, 100]}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        onCellClick={({ row, field }) => {
          // Las columnas editables se quedan en la celda; el resto abre la cuenta.
          if (EDITABLE_FIELDS.includes(field)) return;
          navigate(`/accounts/${row.tenant_id}?source=${row.source_db}`);
        }}
        sx={{
          backgroundColor: '#fff', cursor: 'pointer', border: '1px solid #E4E0E2',
          '& .MuiDataGrid-columnHeaders': { borderBottom: '1px solid #E4E0E2' },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: 11, fontWeight: 600, letterSpacing: '0.07em',
            textTransform: 'uppercase', color: '#847782',
          },
          '& .MuiDataGrid-cell': { borderBottom: '1px solid #F0ECEE' },
          '& .MuiDataGrid-row:hover': { backgroundColor: '#FFF0F0' },
        }}
      />
    </Layout>
  );
}
